// assets/product-hotspots.js
class ProductHotspots {
  constructor(options = {}) {
    this.productId = options.productId;
    this.productHandle = options.productHandle; // Always passed from Liquid template
    this.hotspotsData = options.hotspotsData || {};
    this.activeTooltip = null;
    this.isMobile = window.innerWidth <= 768;
    this.currentImageGid = null;
    this.productCache = new Map(); // Cache for product data
    
    this.init();
  }
  
  init() {
    this.setupGalleryNavigation();
    this.loadInitialImage();
    this.setupEventListeners();
    this.preloadProductData();
  }
  
  setupGalleryNavigation() {
    // Listen for gallery slide changes using Shopify's gallery structure
    const galleryItems = document.querySelectorAll('.product-gallery__carousel-item');

    // Debounce slide changes to prevent Flickity drag from causing hotspot flicker
    let slideChangeTimer = null;

    // Observe changes to the is-selected class
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const item = mutation.target;
          if (item.classList.contains('is-selected')) {
            // Debounce to avoid rapid clear/render during Flickity drag
            clearTimeout(slideChangeTimer);
            slideChangeTimer = setTimeout(() => {
              this.handleSlideChange(item);
            }, 50);
          }
        }
      });
    });

    galleryItems.forEach(item => {
      observer.observe(item, { attributes: true });
    });
    
    // Also listen for thumbnail clicks
    const thumbnails = document.querySelectorAll('.product-gallery__thumbnail');
    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => {
        // Small delay to let the gallery update
        setTimeout(() => {
          // Resolve the slide from the clicked thumbnail's media id — a bare
          // '.is-selected' lookup can grab a stale slide if extra is-selected
          // classes ever exist, clearing hotspots without re-rendering them
          const mediaId = thumbnail.dataset.mediaId;
          const activeItem = (mediaId && document.querySelector(`.product-gallery__carousel-item[data-media-id="${mediaId}"]`)) ||
            document.querySelector('.product-gallery__carousel-item.is-selected');
          if (activeItem) {
            this.handleSlideChange(activeItem);
          }
        }, 100);
      });
    });
  }
  
  handleSlideChange(slideElement) {
    const imageGid = slideElement.dataset.mediaGid;
    const hasHotspots = slideElement.dataset.hasHotspots === 'true';
    
    // Clear existing hotspots
    this.clearHotspots();
    
    // Update current image ID
    this.currentImageGid = imageGid;
    
    if (hasHotspots && imageGid) {
      this.renderHotspotsForImage(imageGid);
    }
  }
  
  loadInitialImage() {
    const activeSlide = document.querySelector('.product-gallery__carousel-item.is-selected');
    if (activeSlide) {
      this.handleSlideChange(activeSlide);
    } else {
      // Fallback - get the first item with hotspots
      const firstHotspotItem = document.querySelector('.product-gallery__carousel-item[data-has-hotspots="true"]');
      if (firstHotspotItem) {
        this.handleSlideChange(firstHotspotItem);
      }
    }
  }
  
  renderHotspotsForImage(imageGid) {
    const imageData = this.hotspotsData[imageGid];
    if (!imageData || !imageData.hotspots) {
      return;
    }
    
    // Get current product handle from instance property
    const currentProductHandle = this.productHandle;
    
    // Try multiple selector approaches to find the container
    let container = document.querySelector(`[data-image-gid="${imageGid}"] .hotspot-container`);
    
    if (!container) {
      // Try finding by the active/selected carousel item
      const activeItem = document.querySelector('.product-gallery__carousel-item.is-selected');
      if (activeItem && activeItem.dataset.mediaGid === imageGid) {
        container = activeItem.querySelector('.hotspot-container');
      }
    }
    
    if (!container) {
      // Last resort - find any container with matching gid
      const allContainers = document.querySelectorAll('.hotspot-container');
      for (const cont of allContainers) {
        if (cont.dataset.imageGid === imageGid) {
          container = cont;
          break;
        }
      }
    }
    
    if (!container) {
      return;
    }
    
    // Sort by order, keep all hotspots including self-product
    const sortedHotspots = imageData.hotspots
      .filter(hotspot => hotspot.productHandle)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    sortedHotspots.forEach((hotspot, index) => {
      const isSelf = hotspot.productHandle === currentProductHandle;
      const hotspotElement = this.createHotspotElement(hotspot, index + 1, isSelf);
      container.appendChild(hotspotElement);
    });
  }
  
  createHotspotElement(hotspot, displayNumber, isSelf = false) {
    const hotspotEl = document.createElement('div');
    hotspotEl.className = isSelf ? 'hotspot hotspot--self' : 'hotspot';
    hotspotEl.style.left = `${hotspot.x}%`;
    hotspotEl.style.top = `${hotspot.y}%`;
    hotspotEl.dataset.hotspotId = hotspot.id;
    hotspotEl.dataset.productHandle = hotspot.productHandle;

    hotspotEl.innerHTML = `${displayNumber}`;

    // Add hover hint for desktop
    if (!this.isMobile) {
      const hint = document.createElement('div');
      hint.className = 'hotspot-hint';
      if (isSelf) {
        hint.textContent = 'This product';
      } else if (hotspot.qtyUsed) {
        hint.textContent = `Qty: ${hotspot.qtyUsed} — Click to view`;
      } else {
        hint.textContent = 'Click to view product';
      }
      hotspotEl.appendChild(hint);
    }

    if (isSelf) {
      // Self-product hotspot: no tooltip, no click interaction
      return hotspotEl;
    }

    // Create tooltip as a separate element in body to avoid CSS inheritance
    const tooltip = document.createElement('div');
    tooltip.className = 'hotspot-tooltip-overlay';
    tooltip.innerHTML = '<div class="tooltip-loading">Loading...</div>';

    // Add to body to avoid CSS inheritance issues
    document.body.appendChild(tooltip);

    // Store reference to tooltip
    hotspotEl._tooltip = tooltip;

    // Load product data on hover/click
    this.setupHotspotInteraction(hotspotEl, hotspot);

    return hotspotEl;
  }
  
  setupHotspotInteraction(hotspotEl, hotspot) {
    const tooltip = hotspotEl._tooltip;
    let isLoaded = false;
    
    const loadProductData = async () => {
      if (isLoaded) return;
      
      try {
        const productData = await this.getProductData(hotspot.productHandle);
        this.renderTooltipContent(tooltip, productData, hotspot);
        isLoaded = true;
      } catch (error) {
        // Silently handle error and show user-friendly message
        tooltip.innerHTML = '<div class="tooltip-error">Product not found</div>';
      }
    };
    
    // Use click for both mobile and desktop for consistency
    hotspotEl.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      loadProductData();
      
      if (this.isMobile) {
        this.toggleMobileTooltip(hotspotEl);
      } else {
        this.toggleDesktopTooltip(hotspotEl);
      }
    });
    
    // Optional: Add hover effect on desktop
    if (!this.isMobile) {
      hotspotEl.addEventListener('mouseenter', () => {
        hotspotEl.classList.add('hotspot-hover');
      });
      
      hotspotEl.addEventListener('mouseleave', () => {
        hotspotEl.classList.remove('hotspot-hover');
      });
    }
  }
  
  positionTooltip(hotspotEl, tooltip) {
    // Get the product gallery carousel wrapper (the main container)
    const galleryWrapper = document.querySelector('.product-gallery__carousel-wrapper');
    if (!galleryWrapper) {
      return;
    }
    
    const wrapperRect = galleryWrapper.getBoundingClientRect();
    const hotspotRect = hotspotEl.getBoundingClientRect();
    
    // Calculate center position within the gallery wrapper
    const centerX = wrapperRect.left + (wrapperRect.width / 2);
    const centerY = wrapperRect.top + (wrapperRect.height / 2);
    
    // Position tooltip at center (subtract half width to center it)
    const tooltipLeft = centerX - 140; // 140px is half of min-width (280px)
    const tooltipTop = centerY - 120; // Adjust vertical position
    
    // Apply positioning without transition
    tooltip.style.setProperty('transition', 'none', 'important');
    tooltip.style.setProperty('position', 'fixed', 'important');
    tooltip.style.setProperty('left', `${tooltipLeft}px`, 'important');
    tooltip.style.setProperty('top', `${tooltipTop}px`, 'important');
    tooltip.style.setProperty('transform', 'none', 'important');
    tooltip.style.setProperty('margin', '0', 'important');
    tooltip.style.setProperty('z-index', '99999', 'important');
    
    // Force a reflow to ensure position is set before transition
    tooltip.offsetHeight;
    
    // Re-enable transition for opacity/visibility
    tooltip.style.removeProperty('transition');
    
    // Calculate arrow position to point to the hotspot
    const arrowLeft = hotspotRect.left + (hotspotRect.width / 2) - tooltipLeft;
    const clampedArrowLeft = Math.max(16, Math.min(arrowLeft, 264)); // Keep arrow within tooltip bounds
    
    // Set arrow position using CSS custom property
    tooltip.style.setProperty('--arrow-left', `${clampedArrowLeft}px`);
    
    // Determine if arrow should be on top or bottom
    const hotspotY = hotspotRect.top + (hotspotRect.height / 2);
    const tooltipCenterY = tooltipTop + 120; // Approximate tooltip center
    
    if (hotspotY < tooltipCenterY) {
      // Hotspot is above tooltip center, put arrow on top
      tooltip.classList.add('arrow-top');
      tooltip.classList.remove('arrow-bottom');
    } else {
      // Hotspot is below tooltip center, put arrow on bottom
      tooltip.classList.add('arrow-bottom');
      tooltip.classList.remove('arrow-top');
    }
  }
  
  showTooltip(tooltip) {
    // Apply visibility styles
    tooltip.style.opacity = '1';
    tooltip.style.visibility = 'visible';
    tooltip.style.pointerEvents = 'auto'; // Changed from 'none' to allow close button
  }
  
  hideTooltip(tooltip) {
    tooltip.style.opacity = '0';
    tooltip.style.visibility = 'hidden';
  }
  
  async getProductData(productHandle) {
    // Check cache first
    if (this.productCache.has(productHandle)) {
      return this.productCache.get(productHandle);
    }
    
    try {
      // Fetch product data from Shopify's AJAX API
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Product not found');
      
      const productData = await response.json();
      
      // Transform to our expected format
      const transformedData = {
        id: productData.id,
        title: productData.title,
        handle: productData.handle,
        price: this.formatPrice(productData.price),
        compareAtPrice: productData.compare_at_price ? this.formatPrice(productData.compare_at_price) : null,
        available: productData.available,
        totalInventory: this.calculateTotalInventory(productData.variants),
        featuredImage: this.getOptimizedImageUrl(productData.featured_image),
        url: `/products/${productData.handle}`
      };
      
      // Cache the result
      this.productCache.set(productHandle, transformedData);
      
      return transformedData;
    } catch (error) {
      throw new Error(`Failed to load product: ${productHandle}`);
    }
  }
  
  getOptimizedImageUrl(imageUrl) {
    if (!imageUrl) return '';
    
    // Check if it's already a Shopify CDN URL
    if (imageUrl.includes('cdn.shopify.com')) {
      // Replace any existing size parameter or add one
      // For thumbnails, 160x160 is sufficient (80px displayed @ 2x for retina)
      return imageUrl.replace(/(_\d+x\d*)?(\.\w+)(\?.*)?$/, '_160x160$2');
    }
    
    // If it's a relative URL, make it absolute and add size
    if (imageUrl.startsWith('//')) {
      return `https:${imageUrl}`.replace(/(\.\w+)(\?.*)?$/, '_160x160$1');
    }
    
    return imageUrl;
  }
  
  renderTooltipContent(tooltip, productData, hotspot) {
    const availability = this.getAvailabilityStatus(productData);
    const displayName = hotspot.customLabel || productData.title;

    // Get current product handle (from page URL or meta tag)
    const currentProductHandle = window.location.pathname.split('/').pop() ||
                                document.querySelector('meta[property="og:url"]')?.content?.split('/').pop();

    // Build tracking URL
    const trackingUrl = `${productData.url}?ref=hotspot&from=${currentProductHandle}`;

    tooltip.innerHTML = `
      <button class="tooltip-close" aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      </button>
      <div class="product-info">
        <img src="${productData.featuredImage}" 
             alt="${productData.title}" 
             class="product-thumbnail"
             loading="lazy"
             width="80"
             height="80">
        <div class="product-details">
          <h4>${displayName}</h4>
          <div class="product-price">
            ${productData.compareAtPrice ? `<span class="compare-price">${productData.compareAtPrice}</span>` : ''}
            ${productData.price}
          </div>
          ${hotspot.qtyUsed ? `<div class="product-qty-used">Qty used: ${hotspot.qtyUsed}</div>` : ''}
          <div class="product-availability ${availability.class}">
            ${availability.text}
          </div>
          <a href="${trackingUrl}" 
             class="cta-button"
             data-hotspot-click="true"
             data-source-product="${currentProductHandle}"
             data-target-product="${productData.handle}"
             data-hotspot-id="${hotspot.id}">
            View Product
          </a>
        </div>
      </div>
    `;

    // Add close button functionality
    const closeBtn = tooltip.querySelector('.tooltip-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.hideTooltip(tooltip);
        if (this.activeTooltip) {
          this.activeTooltip.classList.remove('active');
          this.activeTooltip = null;
        }
      });
    }
  }

  
  getAvailabilityStatus(productData) {
    if (!productData.available) {
      return { class: 'availability-out-of-stock', text: 'Out of stock' };
    }
    
    if (productData.totalInventory <= 3 && productData.totalInventory > 0) {
      return { class: 'availability-low-stock', text: `Only ${productData.totalInventory} left` };
    }
    
    return { class: 'availability-in-stock', text: 'In stock' };
  }
  
  calculateTotalInventory(variants) {
    return variants.reduce((total, variant) => {
      return total + (variant.inventory_quantity || 0);
    }, 0);
  }
  
  formatPrice(priceInCents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(priceInCents / 100);
  }
  
  toggleMobileTooltip(hotspotEl) {
    const tooltip = hotspotEl._tooltip;
    
    // Close any other active tooltips
    if (this.activeTooltip && this.activeTooltip !== hotspotEl) {
      this.activeTooltip.classList.remove('active');
      if (this.activeTooltip._tooltip) {
        this.hideTooltip(this.activeTooltip._tooltip);
      }
    }
    
    hotspotEl.classList.toggle('active');
    
    if (hotspotEl.classList.contains('active')) {
      this.activeTooltip = hotspotEl;
      // Position tooltip for mobile (fixed at bottom of screen)
      this.positionMobileTooltip(tooltip);
      this.showTooltip(tooltip);
    } else {
      this.activeTooltip = null;
      this.hideTooltip(tooltip);
    }
  }
  
  positionMobileTooltip(tooltip) {
    // For mobile, position at bottom of screen
    tooltip.style.position = 'fixed';
    tooltip.style.bottom = '20px';
    tooltip.style.left = '20px';
    tooltip.style.right = '20px';
    tooltip.style.top = 'auto';
    tooltip.style.width = 'auto';
    tooltip.style.minWidth = 'auto';
    tooltip.style.maxWidth = 'none';
    tooltip.style.margin = '0';
    tooltip.style.transform = 'translateY(0)';
    tooltip.style.zIndex = '99999';
    tooltip.style.background = 'rgba(255, 255, 255, 0.85)';
    tooltip.style.border = '1px solid rgba(221, 221, 221, 0.8)';
    tooltip.style.borderRadius = '8px';
    tooltip.style.padding = '32px 16px 16px 16px';
    tooltip.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
    tooltip.style.backdropFilter = 'blur(8px)';
    tooltip.style.webkitBackdropFilter = 'blur(8px)';
    
    // Remove arrow classes for mobile
    tooltip.classList.remove('arrow-top', 'arrow-bottom');
  }
  
  clearHotspots() {
    const allContainers = document.querySelectorAll('.hotspot-container');
    allContainers.forEach(container => {
      // Clean up tooltips before clearing containers
      const hotspots = container.querySelectorAll('.hotspot');
      hotspots.forEach(hotspot => {
        if (hotspot._tooltip) {
          document.body.removeChild(hotspot._tooltip);
        }
      });
      container.innerHTML = '';
    });
    
    if (this.activeTooltip) {
      this.activeTooltip.classList.remove('active');
      this.activeTooltip = null;
    }
  }
  
  preloadProductData() {
    // Preload product data for current image hotspots
    if (this.currentImageGid && this.hotspotsData[this.currentImageGid]) {
      const hotspots = this.hotspotsData[this.currentImageGid].hotspots || [];
      hotspots.slice(0, 2).forEach(hotspot => { // Preload first 2 products
        this.getProductData(hotspot.productHandle).catch(() => {
          // Silent fail for preloading
        });
      });
    }
  }
  
  toggleDesktopTooltip(hotspotEl) {
    const tooltip = hotspotEl._tooltip;
    
    // Close any other active tooltips
    if (this.activeTooltip && this.activeTooltip !== hotspotEl) {
      this.activeTooltip.classList.remove('active');
      if (this.activeTooltip._tooltip) {
        this.hideTooltip(this.activeTooltip._tooltip);
      }
    }
    
    hotspotEl.classList.toggle('active');
    
    if (hotspotEl.classList.contains('active')) {
      this.activeTooltip = hotspotEl;
      this.positionTooltip(hotspotEl, tooltip);
      this.showTooltip(tooltip);
    } else {
      this.activeTooltip = null;
      this.hideTooltip(tooltip);
    }
  }
  
  setupEventListeners() {
    // Close tooltips when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.hotspot') && !e.target.closest('.hotspot-tooltip-overlay') && this.activeTooltip) {
        this.activeTooltip.classList.remove('active');
        if (this.activeTooltip._tooltip) {
          this.hideTooltip(this.activeTooltip._tooltip);
        }
        this.activeTooltip = null;
      }
    });
    
    // Handle window resize
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;
      
      // If switching from mobile to desktop or vice versa, clear active tooltips
      if (wasMobile !== this.isMobile && this.activeTooltip) {
        this.activeTooltip.classList.remove('active');
        if (this.activeTooltip._tooltip) {
          this.hideTooltip(this.activeTooltip._tooltip);
        }
        this.activeTooltip = null;
      }
    });
  }
}

// Make globally available
window.ProductHotspots = ProductHotspots;